import { getErrorMessage } from '@core/utils/api/errorMessage';
import axios from '@core_utils/api/_axios';

const ai_assessment = {
    namespaced: true,
    state: {
        aiEvData: null,
        aiEvDataFetchLoading: false,
        // myRatings[ai_audit_id][ev_id] = rating object | null
        // null = 명시적으로 "채점 없음"으로 조회됨, undefined = 미조회
        myRatings: {},
    },
    getters: {
        subAiEvData: (state) => (sub_id) => {
            if (!state.aiEvData) {
                return null;
            }
            return state.aiEvData[sub_id] || null;
        },
        getMyRating: (state) => (ai_audit_id, ev_id) => {
            if (ai_audit_id == null || ev_id == null) return null;
            return state.myRatings?.[ai_audit_id]?.[ev_id] ?? null;
        },
    },
    actions: {
        setSubAiEvData: ({state, commit}, {aiEvData, sub_id}) => {
            if (!state.aiEvData)  {
                commit('set_ai_ev_data', {});
            }
            const temp = JSON.parse(JSON.stringify(state.aiEvData));
            temp[sub_id] = aiEvData[sub_id];
            commit('set_ai_ev_data', temp);
        },
        setAiEvData: ({commit}, aiEvData) => {
            commit('set_ai_ev_data', aiEvData);
        },
        async fetchAiEvData({state, commit}, {sub_id, loadingControl = true}) {
            try {
                if(loadingControl)
                    state.aiEvDataFetchLoading = true;
                const aiEvRes = await axios.get('/assessments/ai-audit/sub', {
                    params: {sub_id}
                });
                
                if(!aiEvRes.data) {
                    return null;
                }
                
                const aiEvData = {};
                // SubId를 하나로 조회했으므로 ai_audits는 하나만 존재
                Object.entries(aiEvRes.data).forEach(([sub_id,ai_audits]) => {
                    const subAiEvData =  {};
                    ai_audits.forEach((ai_audit, index) => {
                        ai_audit.valid_json?.forEach(ev_item => {
                            if(ev_item.reason == null){
                                ev_item.converted_reason = {
                                    audit_category_code: null,
                                    audit_comment: null,
                                };
                                return;
                            }
                            const basisTexts = ev_item.reason?.evidence.map((item, index) => {
                                const fileLine = `${index + 1}. ${item.filename}`;
                                const basisLine = `  - ${item.basis}`; // 'basis' 내용 앞에 공백 두 칸으로 들여쓰기
                                return `${fileLine}\n${basisLine}`;
                            }) ?? [];
                            
                            let combinedBasis = '';
                            if(basisTexts.length !== 0){
                                combinedBasis = basisTexts.join('\n\n'); // 근거 항목이 여러 개일 경우 사이를 한 줄 띄웁니다.
                                combinedBasis = `[근거]\n${combinedBasis}\n---\n`;
                                
                            }
                            const commentText = ev_item.reason.comment;
                            
                            const result_comment = `${combinedBasis}[총평]\n${commentText}`;
                            ev_item.converted_reason = {
                                audit_category_code: ev_item.reason.category.code,
                                audit_comment: result_comment
                            };
                        });
                        ai_audit.valid_map = ai_audit.valid_json?.reduce((acc, item) => {
                            acc[item.ev_id] = item;
                            return acc;
                        }, {}) ?? {};
                        ai_audit.version = `v${index + 1}`;
                        subAiEvData[ai_audit.id] = ai_audit;
                    });
                    aiEvData[sub_id] = subAiEvData;
                });
                
                return aiEvData;
            }
            catch (error) {
                if(getErrorMessage(error, '') === 'AI Audit entries not found.') {
                    return null;
                }
            }
            finally {
                if(loadingControl)
                    state.aiEvDataFetchLoading = false;
            }
            
        },
        async requestSubAiEvaluation(_context, {companyId, campaignId, subCode, judgmentModel, judgmentMode = 'immediate'}) {
            const payload = {
                company_id: companyId,
                campaign_id: campaignId,
                sub_code: subCode,
            };
            if (judgmentModel) {
                payload.judgment_model = judgmentModel;
            }
            if (judgmentModel || judgmentMode === 'batch') payload.judgment_mode = judgmentMode;
            return axios.post('/assessments/ai-audit', payload);
        },
        async retryAiEvaluationItem(_context, {auditId, evId}) {
            return axios.post(`/assessments/ai-audits/${auditId}/evaluations/${evId}/retry`);
        },
        async refreshSubAiEvData({state, dispatch, commit}, {subId}) {
            const normalized = await dispatch('fetchAiEvData', {
                sub_id: subId,
                loadingControl: false,
            });
            if (normalized) {
                commit('set_ai_ev_data', {...(state.aiEvData || {}), ...normalized});
            }
            return normalized;
        },
        async fetchMyRatingsForAudit({commit}, {ai_audit_id}) {
            if (ai_audit_id == null) return {};
            try {
                const res = await axios.get(`/v2/ai-evaluation/${ai_audit_id}/ratings`);
                const map = res?.data?.ratings ?? {};
                commit('set_my_ratings_for_audit', {ai_audit_id, map});
                return map;
            } catch (err) {
                // 백엔드 미구현 단계(404)나 권한(403)은 조용히 빈 맵 반환
                if ([403, 404, 501].includes(err?.response?.status)) {
                    commit('set_my_ratings_for_audit', {ai_audit_id, map: {}});
                    return {};
                }
                throw err;
            }
        },
        async fetchMyRatingsForAudits({dispatch}, {ids}) {
            if (!Array.isArray(ids) || ids.length === 0) return {};
            // Promise.all로 병렬 호출. 개별 실패는 fetchMyRatingsForAudit 내부에서
            // 403/404/501을 swallow하므로 Promise.all 자체 실패는 예외적 상황만.
            const pairs = await Promise.all(
                ids.map(async (id) => {
                    try {
                        const map = await dispatch('fetchMyRatingsForAudit', {ai_audit_id: id});
                        return [id, map || {}];
                    } catch (_err) {
                        return [id, {}];
                    }
                })
            );
            return Object.fromEntries(pairs);
        },
        updateMyRatingCache({commit}, {ai_audit_id, ev_id, rating}) {
            commit('set_my_rating_entry', {ai_audit_id, ev_id, rating});
        },
    },
    mutations: {
        set_ai_ev_data(state, aiEvData) {
            state.aiEvData = aiEvData;
        },
        set_ai_ev_data_fetch_loading(state, loading) {
            state.aiEvDataFetchLoading = loading;
        },
        set_my_ratings_for_audit(state, {ai_audit_id, map}) {
            state.myRatings = {
                ...state.myRatings,
                [ai_audit_id]: map || {},
            };
        },
        set_my_rating_entry(state, {ai_audit_id, ev_id, rating}) {
            const current = state.myRatings?.[ai_audit_id] || {};
            state.myRatings = {
                ...state.myRatings,
                [ai_audit_id]: {
                    ...current,
                    [ev_id]: rating,
                },
            };
        },
        reset_my_ratings(state) {
            state.myRatings = {};
        },
    },
};
export default ai_assessment;
